import React, { Component } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import bluePin from '../../../assets/blue.svg';
import greenPin from '../../../assets/green.svg';
import { isPublicMoorageListing } from '../../../util/listingHelpers';

import css from './SearchMapGroupLabel.module.css';

export const GROUPED_PIN_PRIVATE = 'private';
export const GROUPED_PIN_PUBLIC = 'public';
export const GROUPED_PIN_MIXED = 'mixed';

export const getGroupedPinVariant = listings => {
  const listingArray = Array.isArray(listings) ? listings : [];
  const publicListingCount = listingArray.filter(listing =>
    isPublicMoorageListing(listing?.attributes?.publicData)
  ).length;

  if (publicListingCount === listingArray.length && listingArray.length > 0) {
    return GROUPED_PIN_PUBLIC;
  }

  if (publicListingCount === 0) {
    return GROUPED_PIN_PRIVATE;
  }

  return GROUPED_PIN_MIXED;
};

export const formatGroupedPinCount = count => {
  const numericCount = Number(count);
  return Number.isFinite(numericCount) && numericCount > 99 ? '99+' : String(count || 0);
};

/**
 * SearchMapGroupLabel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {boolean} [props.isActive] - The active status
 * @param {string} [props.mapComponentRefreshToken] - The map component refresh token
 * @returns {JSX.Element}
 */
class SearchMapGroupLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const hasSameAmountOfListings = nextProps.listings.length === this.props.listings.length;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const hasSameVariant =
      getGroupedPinVariant(nextProps.listings) === getGroupedPinVariant(this.props.listings);

    return !(
      hasSameAmountOfListings &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSameVariant
    );
  }

  render() {
    const { className, rootClassName, listings, onListingClicked, isActive } = this.props;
    const variant = getGroupedPinVariant(listings);
    const countLabel = formatGroupedPinCount(listings.length);
    const pinImage = variant === GROUPED_PIN_PUBLIC ? greenPin : bluePin;
    const pinAlt =
      variant === GROUPED_PIN_PUBLIC
        ? 'Grouped public moorages map pin'
        : variant === GROUPED_PIN_PRIVATE
        ? 'Grouped private moorages map pin'
        : 'Grouped mixed moorages map pin';
    const classes = classNames(rootClassName || css.root, className, {
      [css.rootActive]: isActive,
    });
    const countLabelClasses = classNames(css.countBadge, {
      [css.countBadgeLarge]: countLabel.length > 2,
    });

    return (
      <button
        type="button"
        aria-label={`${countLabel} grouped listings`}
        className={classes}
        onClick={e => {
          e.stopPropagation();
          e.nativeEvent?.stopImmediatePropagation?.();
          onListingClicked(listings);
        }}
      >
        <span className={css.pinWrapper}>
          <img className={css.pinImage} src={pinImage} alt={pinAlt} />
          <span className={countLabelClasses}>{countLabel}</span>
        </span>
      </button>
    );
  }
}

export default SearchMapGroupLabel;
